import { cn } from "@u/cn";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { DayPicker } from "react-day-picker";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("relative p-3 pt-4", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-4",
        month: "relative flex flex-col gap-4 w-full",
        // pointer-events-none: the centered caption spans full width and would
        // otherwise swallow clicks on the nav chevrons sitting under it.
        month_caption:
          "flex h-8 items-center justify-center relative w-full pointer-events-none",
        caption_label: "text-sm font-medium capitalize",
        // Nav overlays the caption row: prev pinned left, next right, month
        // label centered on the same line.
        nav: "absolute inset-x-0 top-4 z-10 flex h-8 items-center justify-between px-3",
        button_previous: cn(
          "h-7 w-7 rounded-lg border border-border/30 bg-muted/20",
          "hover:bg-muted/40 flex items-center justify-center transition-colors",
          "disabled:opacity-30 disabled:pointer-events-none",
        ),
        button_next: cn(
          "h-7 w-7 rounded-lg border border-border/30 bg-muted/20",
          "hover:bg-muted/40 flex items-center justify-center transition-colors",
          "disabled:opacity-30 disabled:pointer-events-none",
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday: "text-muted-foreground rounded-md flex-1 text-[0.75rem] font-normal text-center",
        week: "flex w-full mt-2",
        day: "flex-1 text-center text-sm p-0 relative",
        day_button: cn(
          "mx-auto h-10 w-10 sm:h-8 sm:w-8 rounded-lg text-sm transition-all duration-200",
          "hover:bg-muted/40 flex items-center justify-center",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40",
        ),
        selected: "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary/90",
        today: "[&>button]:border [&>button]:border-border/50",
        outside: "[&>button]:text-muted-foreground [&>button]:hover:bg-transparent [&>button]:cursor-default",
        disabled: "[&>button]:text-muted-foreground [&>button]:opacity-40 [&>button]:cursor-not-allowed [&>button]:hover:bg-transparent",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === "left" ? (
            <ChevronLeft size={14} />
          ) : (
            <ChevronRight size={14} />
          ),
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
